/*
 * AvailabilityForm — the create/edit form rendered inside the modal.
 * State lives on the parent (single source of truth); this component
 * is presentational + wires the field-level change handlers.
 */
import { ActionButton, GhostButton, FormField, inputClass } from "../Primitives";
import { SingleSelectDropdown } from "../SingleSelectDropdown";
import { KIND_OPTIONS } from "./lib";
import { SplitRangeSection } from "./SplitRangeSection";

export function AvailabilityForm({
  form, setForm,
  editing,
  isOp,
  guards,
  reasonOptions,
  onSubmit,
  onCancel,
  onSplitRemove,
  onSplitEdit,
}) {
  const toggleReason = (r) => {
    setForm((f) => ({
      ...f,
      reasons: f.reasons.includes(r) ? f.reasons.filter((x) => x !== r) : [...f.reasons, r],
    }));
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {isOp && !editing && (
        <FormField label="Officer *">
          <SingleSelectDropdown
            testId="avail-guard"
            label="Search & pick a "
            options={guards
              .filter((g) => (g.status || "active") === "active")
              .map((g) => ({
                value: g.id,
                label: g.name || "Unknown",
                sub: g.badge_no ? `#${g.badge_no}` : "",
              }))}
            value={form.guard_id}
            onChange={(v) => setForm({ ...form, guard_id: v })}
            searchable
            triggerClassName="w-full"
          />
        </FormField>
      )}

      {!editing && (
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Start date *">
            <input type="date" className={inputClass} data-testid="avail-start-date"
              value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required />
          </FormField>
          <FormField label="End date *">
            <input type="date" className={inputClass} data-testid="avail-end-date"
              value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} required />
          </FormField>
        </div>
      )}
      {editing && (
        <FormField label={editing.count > 1 ? `Range (${editing.count} days)` : "Date"}>
          <div className="grid grid-cols-2 gap-3">
            <input type="date" className={`${inputClass} opacity-60`} value={form.start_date} disabled data-testid="avail-edit-start-date" />
            <input type="date" className={`${inputClass} opacity-60`} value={form.end_date} disabled data-testid="avail-edit-end-date" />
          </div>
          <div className="text-[10px] text-zinc-500 mt-1 uppercase tracking-widest">
            {editing.count > 1
              ? `Applies to all ${editing.count} days in this range. Delete + re-create to move dates.`
              : "Delete + re-create the entry to move it to a different day."}
          </div>
        </FormField>
      )}

      <SplitRangeSection
        editing={editing}
        onRemoveDay={onSplitRemove}
        onEditDay={onSplitEdit}
      />

      <div className="grid grid-cols-2 gap-3">
        <FormField label="Window start (blank = all day)">
          <input type="time" className={inputClass} data-testid="avail-window-start"
            value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
        </FormField>
        <FormField label="Window end">
          <input type="time" className={inputClass} data-testid="avail-window-end"
            value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
        </FormField>
      </div>

      <FormField label="Kind *">
        <select
          data-testid="avail-kind-select"
          className={inputClass}
          value={form.kind}
          onChange={(e) => setForm({
            ...form,
            kind: e.target.value,
            reasons: e.target.value === "available" ? [] : form.reasons,
          })}
        >
          {KIND_OPTIONS.map((k) => (
            <option key={k.value} value={k.value}>{k.label}</option>
          ))}
        </select>
      </FormField>

      {form.kind === "unavailable" && (
        <FormField label="Reason *">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5" data-testid="avail-reasons">
            {reasonOptions.map((r) => {
              const active = form.reasons.includes(r);
              return (
                <label
                  key={r}
                  data-testid={`avail-reason-${r.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-widest border px-2 py-1.5 cursor-pointer transition-colors ${
                    active
                      ? "border-[#FF3B30] text-[#FF3B30] bg-[#FF3B30]/5"
                      : "border-[#27272A] text-zinc-400 hover:text-white"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="accent-[#FF3B30]"
                    checked={active}
                    onChange={() => toggleReason(r)}
                  />
                  {r}
                </label>
              );
            })}
          </div>
          <div className="text-[10px] text-zinc-500 mt-1.5 uppercase tracking-widest">
            Pick one or more — helps dispatch understand the absence.
          </div>
        </FormField>
      )}

      <FormField label="Notes">
        <input className={inputClass} data-testid="avail-notes"
          value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </FormField>

      <div className="flex justify-end gap-2 pt-2 border-t border-[#27272A]">
        <GhostButton type="button" onClick={onCancel}>Cancel</GhostButton>
        <ActionButton type="submit" data-testid="avail-save">
          {editing ? "Save changes" : "Create"}
        </ActionButton>
      </div>
    </form>
  );
}
